import React, { useMemo, useState } from 'react';
import { joinFindingValues } from '@/features/report/Findings/FindingInput/utils/statements';
import {
  Modal,
  Steps,
  Typography,
  Tag,
  Space,
  Collapse,
  Table,
  Empty,
  Divider,
} from 'antd';
import moment from 'moment';
import { mergeReportVersions } from './utils/reportHistoryMerge';

const { Text } = Typography;

interface ReportsPagination {
  count: number;
  next: any;
  previous: any;
  results: ReportVersion[];
}

interface ReportVersion {
  uuid: string;
  studyUuid: string;
  status: 'DRAFT' | 'PRELIMINARY' | 'FINAL';
  createdAt: string;
  updatedAt: string;
  conclusion?: {
    uuid: string;
    value: string;
    createdAt?: string;
    updatedAt?: string;
  } | null;

  signature?: {
    createdAt: string;
    signerFirstName: string;
    signerLastName: string;
    signerUuid: string;
    uuid: string;
    report: string;
  } | null;

  addendum?: {
    createdAt: string;
    updatedAt: string;
    description: string;
    reason: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    uuid: string;
  } | null;

  findings: {
    findingClass: string;
    reportFindingClassValue: string;
    reportFindingClassDescription: string;
    value: string;
    uuid: string;
    index: number;
  }[];

  measurements: {
    canonicalMeasurement: string;
    reportMeasurementClassValue: string;
    reportMeasurementClassDescription: string;
    value: number;
    uuid: string;
  }[];
}

type HistoryEvent =
  | {
      kind: 'REPORT_CREATED';
      ts: string;
      title: string;
      subtitle?: string;
      report: ReportVersion;
    }
  | {
      kind: 'REPORT_FINALIZED';
      ts: string;
      title: string;
      subtitle?: string;
      report: ReportVersion;
    }
  | {
      kind: 'ADDENDUM_CREATED';
      ts: string;
      title: string;
      subtitle?: string;
      report: ReportVersion;
      addendum: NonNullable<ReportVersion['addendum']>;
      addendumIndex: number; // 1..N
    };

function fmt(iso?: string) {
  if (!iso) return '—';
  return moment(iso).format('MMM D, YYYY h:mm:ss A');
}

function displayNameFromAddendum(a?: ReportVersion['addendum'] | null) {
  if (!a) return 'Unknown';
  const name = `${a.userFirstName || ''} ${a.userLastName || ''}`.trim();
  return name || a.userEmail || 'Unknown';
}

function displayNameFromSignature(s?: ReportVersion['signature'] | null) {
  if (!s) return 'Unknown';
  const name = `${s.signerFirstName || ''} ${s.signerLastName || ''}`.trim();
  return name || 'Unknown';
}

/**
 * Build a stable "version snapshot" that we can diff.
 */
function toSnapshot(r: ReportVersion) {
  const meas = new Map<string, number | null>();
  (r.measurements ?? []).forEach((m) => {
    meas.set(
      m.canonicalMeasurement,
      typeof m.value === 'number' ? m.value : (m.value as any)
    );
  });

  const findings = joinFindingValues(r.findings);

  return {
    uuid: r.uuid,
    conclusions: (r.conclusion?.value ?? '').trim(),
    measurements: meas,
    findings,
  };
}

function diffSnapshots(
  prev: ReturnType<typeof toSnapshot>,
  next: ReturnType<typeof toSnapshot>
) {
  // Measurements diff
  const allMeasKeys = new Set<string>([
    ...Array.from(prev.measurements.keys()),
    ...Array.from(next.measurements.keys()),
  ]);

  const measChanges: {
    key: string;
    label: string;
    before: number | null | undefined;
    after: number | null | undefined;
  }[] = [];

  allMeasKeys.forEach((k) => {
    const before = prev.measurements.get(k);
    const after = next.measurements.get(k);
    if (before !== after) {
      // try to show a friendly label by looking up in "next" report values
      const nextLabel =
        (next as any).__labelLookupMeas?.get(k) ||
        (prev as any).__labelLookupMeas?.get(k) ||
        k;
      measChanges.push({ key: k, label: nextLabel, before, after });
    }
  });

  // Findings diff
  const allFindKeys = new Set<string>([
    ...Array.from(prev.findings.keys()),
    ...Array.from(next.findings.keys()),
  ]);

  const findingChanges: {
    key: string;
    label: string;
    before: string | undefined;
    after: string | undefined;
  }[] = [];

  allFindKeys.forEach((k) => {
    const before = prev.findings.get(k) ?? '';
    const after = next.findings.get(k) ?? '';
    if (before !== after) {
      const nextLabel =
        (next as any).__labelLookupFind?.get(k) ||
        (prev as any).__labelLookupFind?.get(k) ||
        k;
      findingChanges.push({ key: k, label: nextLabel, before, after });
    }
  });

  const conclusionsChanged = prev.conclusions !== next.conclusions;

  return { measChanges, findingChanges, conclusionsChanged };
}

/**
 * Adds label lookup maps onto snapshot so diffs can show human labels instead of UUIDs.
 */
function snapshotWithLabels(r: ReportVersion) {
  const snap = toSnapshot(r) as any;

  const labelLookupMeas = new Map<string, string>();
  (r.measurements ?? []).forEach((m) => {
    labelLookupMeas.set(
      m.canonicalMeasurement,
      m.reportMeasurementClassValue ||
        m.reportMeasurementClassDescription ||
        m.canonicalMeasurement
    );
  });

  const labelLookupFind = new Map<string, string>();
  (r.findings ?? []).forEach((f) => {
    labelLookupFind.set(
      f.findingClass,
      f.reportFindingClassValue ||
        f.reportFindingClassDescription ||
        f.findingClass
    );
  });

  snap.__labelLookupMeas = labelLookupMeas;
  snap.__labelLookupFind = labelLookupFind;

  return snap as ReturnType<typeof toSnapshot>;
}

export default function ReportHistory({
  open,
  onClose,
  reportsPagination,
}: {
  open: boolean;
  onClose: () => void;
  reportsPagination: ReportsPagination | null | undefined;
}) {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const sortedReports = useMemo(() => {
    const arr = (reportsPagination?.results ?? []).slice();
    // oldest -> newest by createdAt
    arr.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    return arr;
  }, [reportsPagination]);

  const events: HistoryEvent[] = useMemo(() => {
    if (!sortedReports.length) return [];

    const out: HistoryEvent[] = [];

    // 1) Created = earliest report (we don't have creator in your payload)
    const first = sortedReports[0];
    out.push({
      kind: 'REPORT_CREATED',
      ts: first.createdAt,
      title: 'Report created',
      subtitle: `Created at ${fmt(first.createdAt)}`,
      report: first,
    });

    // 2) Addendums = any report that has addendum (each is a version)
    let addendumIndex = 0;

    sortedReports.forEach((r) => {
      if (r.addendum) {
        addendumIndex += 1;
        const who = displayNameFromAddendum(r.addendum);
        out.push({
          kind: 'ADDENDUM_CREATED',
          ts: r.addendum.createdAt || r.createdAt,
          title: `Addendum #${addendumIndex}`,
          subtitle: `${who} • ${fmt(r.addendum.createdAt || r.createdAt)} • ${r.addendum.reason}`,
          report: r,
          addendum: r.addendum,
          addendumIndex,
        });
      }
    });

    // 3) Finalized = first report with signature (chronologically)
    const signed = sortedReports.find((r) => !!r.signature);
    if (signed?.signature?.createdAt) {
      const who = displayNameFromSignature(signed.signature);
      out.push({
        kind: 'REPORT_FINALIZED',
        ts: signed.signature.createdAt,
        title: 'Report finalized',
        subtitle: `${who} • ${fmt(signed.signature.createdAt)}`,
        report: signed,
      });
    }

    // ensure chronological by ts
    out.sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());

    return out;
  }, [sortedReports]);

  const stepsItems = useMemo(
    () =>
      events.map((e) => {
        const tagMap: Record<string, React.ReactNode> = {
          REPORT_FINALIZED: <Tag color="green">FINALIZED</Tag>,
          ADDENDUM_CREATED: <Tag color="blue">ADDENDUM</Tag>,
        };
        const tag = tagMap[e.kind] || <Tag>CREATED</Tag>;

        const desc =
          e.kind === 'ADDENDUM_CREATED' ? (
            <Space direction="vertical" size={2}>
              <Text type="secondary">{e.subtitle}</Text>
              <Text>{(e.addendum.description ?? '').trim() || '—'}</Text>
            </Space>
          ) : (
            <Text type="secondary">{e.subtitle}</Text>
          );

        return {
          title: (
            <Space size={8} wrap>
              {tag}
              <Text strong>{e.title}</Text>
            </Space>
          ),
          description: desc,
        };
      }),
    [events]
  );

  const selectedReport = useMemo(() => {
    const ev = events[selectedIndex];
    return ev?.report ?? null;
  }, [events, selectedIndex]);

  const selectedReportIndex = useMemo(() => {
    if (!selectedReport) return -1;
    // History rows can occasionally include repeated UUID snapshots; since
    // selectedReport is a reference from sortedReports, indexOf is safest.
    return sortedReports.indexOf(selectedReport);
  }, [sortedReports, selectedReport]);

  const cumulativeBefore = useMemo(() => {
    if (selectedReportIndex <= 0) return null;
    return mergeReportVersions(sortedReports.slice(0, selectedReportIndex));
  }, [sortedReports, selectedReportIndex]);

  const cumulativeAfter = useMemo(() => {
    if (selectedReportIndex < 0) return null;
    return mergeReportVersions(sortedReports.slice(0, selectedReportIndex + 1));
  }, [sortedReports, selectedReportIndex]);

  const diff = useMemo(() => {
    if (selectedReportIndex <= 0 || !cumulativeBefore || !cumulativeAfter) {
      return null;
    }

    const prevSnap = snapshotWithLabels(cumulativeBefore);
    const nextSnap = snapshotWithLabels(cumulativeAfter);

    return diffSnapshots(prevSnap, nextSnap);
  }, [cumulativeBefore, cumulativeAfter, selectedReportIndex]);

  return (
    <Modal
      title="Report History"
      open={open}
      onCancel={onClose}
      onOk={onClose}
      okText="Close"
      width={980}
      destroyOnHidden
    >
      {!events.length ? (
        <Empty description="No report history available." />
      ) : (
        <>
          <Steps
            direction="vertical"
            current={selectedIndex}
            onChange={(idx) => setSelectedIndex(idx)}
            items={stepsItems as any}
          />

          <Divider style={{ margin: '16px 0' }} />

          <Space direction="vertical" style={{ width: '100%' }} size={10}>
            <Text strong>Selected version</Text>
            <Space wrap>
              <Tag>Report UUID: {selectedReport?.uuid}</Tag>
              <Tag
                color={
                  { FINAL: 'green', PRELIMINARY: 'gold', DRAFT: 'default' }[
                    selectedReport?.status ?? ''
                  ] || 'default'
                }
              >
                {selectedReport?.status}
              </Tag>
              <Tag>Created: {fmt(selectedReport?.createdAt)}</Tag>
              <Tag>Updated: {fmt(selectedReport?.updatedAt)}</Tag>
            </Space>

            {/* Diff vs merged prior worksheet state (supports sparse addendum payloads) */}
            {selectedReportIndex <= 0 || !diff ? (
              <Text type="secondary">
                Select a version after the initial report to see net worksheet
                changes introduced up to that point (addendum rows merge with
                prior values).
              </Text>
            ) : (
              <Collapse
                defaultActiveKey={['meas', 'find', 'conc']}
                items={[
                  {
                    key: 'meas',
                    label: `Measurements changes (${diff.measChanges.length})`,
                    children:
                      diff.measChanges.length === 0 ? (
                        <Text type="secondary">No measurement changes.</Text>
                      ) : (
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={diff.measChanges.map((c) => ({
                            ...c,
                            key: c.key,
                          }))}
                          columns={[
                            {
                              title: 'Measurement',
                              dataIndex: 'label',
                              key: 'label',
                            },
                            {
                              title: 'Before',
                              dataIndex: 'before',
                              key: 'before',
                              align: 'right' as const,
                              render: (v: any) =>
                                v === null || v === undefined ? '—' : String(v),
                            },
                            {
                              title: 'After',
                              dataIndex: 'after',
                              key: 'after',
                              align: 'right' as const,
                              render: (v: any) => (
                                <span style={{ fontWeight: 600 }}>
                                  {v === null || v === undefined
                                    ? '—'
                                    : String(v)}
                                </span>
                              ),
                            },
                          ]}
                        />
                      ),
                  },
                  {
                    key: 'find',
                    label: `Findings changes (${diff.findingChanges.length})`,
                    children:
                      diff.findingChanges.length === 0 ? (
                        <Text type="secondary">No finding changes.</Text>
                      ) : (
                        <Table
                          size="small"
                          pagination={false}
                          dataSource={diff.findingChanges.map((c) => ({
                            ...c,
                            key: c.key,
                          }))}
                          columns={[
                            {
                              title: 'Finding',
                              dataIndex: 'label',
                              key: 'label',
                            },
                            {
                              title: 'Before',
                              dataIndex: 'before',
                              key: 'before',
                              render: (v: string) => v || '—',
                            },
                            {
                              title: 'After',
                              dataIndex: 'after',
                              key: 'after',
                              render: (v: string) => (
                                <span style={{ fontWeight: 600 }}>
                                  {v || '—'}
                                </span>
                              ),
                            },
                          ]}
                        />
                      ),
                  },
                  {
                    key: 'conc',
                    label: `Conclusions changes (${diff.conclusionsChanged ? 1 : 0})`,
                    children: !diff.conclusionsChanged ? (
                      <Text type="secondary">No conclusions change.</Text>
                    ) : (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Text type="secondary">Before (merged prior)</Text>
                        <div
                          style={{
                            padding: 10,
                            border: '1px solid #eee',
                            borderRadius: 8,
                          }}
                        >
                          {(cumulativeBefore?.conclusion?.value ?? '').trim() ||
                            '—'}
                        </div>

                        <Text type="secondary">
                          After (through this version)
                        </Text>
                        <div
                          style={{
                            padding: 10,
                            border: '1px solid #eee',
                            borderRadius: 8,
                          }}
                        >
                          {(cumulativeAfter?.conclusion?.value ?? '').trim() ||
                            '—'}
                        </div>
                      </Space>
                    ),
                  },
                ]}
              />
            )}
          </Space>
        </>
      )}
    </Modal>
  );
}
